import { motion } from "framer-motion";

/**
 * Reveal — wraps children with a fade + translate-up entrance animation
 * triggered the first time the element scrolls into view. Use for section
 * blocks, headings, paragraphs, cards.
 */
export function Reveal({
  children,
  delay = 0,
  duration = 0.7,
  y = 28,
  amount = 0.2,
  className = "",
  as: Tag = "div",
  ...rest
}) {
  const MotionTag = motion[Tag] || motion.div;
  return (
    <MotionTag
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount }}
      transition={{ duration, delay, ease: [0.2, 0.8, 0.2, 1] }}
      className={className}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}

/**
 * StaggerGroup — animates children one after another in cascade.
 */
export function StaggerGroup({ children, stagger = 0.08, className = "", amount = 0.15 }) {
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount }}
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: stagger } },
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export const StaggerItem = ({ children, y = 22, className = "", ...rest }) => (
  <motion.div
    variants={{
      hidden: { opacity: 0, y },
      visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.2, 0.8, 0.2, 1] } },
    }}
    className={className}
    {...rest}
  >
    {children}
  </motion.div>
);

/**
 * Parallax — translates an element upward as it scrolls into view.
 */
export function Parallax({ children, distance = 60, className = "" }) {
  return (
    <motion.div
      initial={{ y: distance }}
      whileInView={{ y: 0 }}
      viewport={{ once: false, amount: 0.1 }}
      transition={{ duration: 1.2, ease: [0.2, 0.8, 0.2, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}
